import { useEffect, useMemo, useRef, useState } from 'react'
import type { DecodedImage, DetectionRegion, ExportOptions, Rect } from '../types'
import { clampRect, isUsableRect, normalizeRect, rectContains } from '../core/rect'
import { createId } from '../core/id'
import { renderRedactedCanvas } from '../core/redaction'

interface Props {
  decoded: DecodedImage
  regions: DetectionRegion[]
  selectedId?: string
  manualMode: boolean
  zoom: number
  options: ExportOptions
  showOriginal: boolean
  onSelect: (id?: string) => void
  onCommit: (regions: DetectionRegion[]) => void
}

type Handle = 'nw' | 'ne' | 'sw' | 'se'
type Interaction =
  | { kind: 'create'; startX: number; startY: number; regionId: string }
  | { kind: 'move'; startX: number; startY: number; initial: Rect; regionId: string }
  | { kind: 'resize'; startX: number; startY: number; initial: Rect; regionId: string; handle: Handle }

function cloneRegions(regions: DetectionRegion[]): DetectionRegion[] {
  return regions.map((region) => ({ ...region, rect: { ...region.rect } }))
}

function findHandle(region: DetectionRegion, x: number, y: number, tolerance: number): Handle | null {
  const points: Array<[Handle, number, number]> = [
    ['nw', region.rect.x, region.rect.y],
    ['ne', region.rect.x + region.rect.width, region.rect.y],
    ['sw', region.rect.x, region.rect.y + region.rect.height],
    ['se', region.rect.x + region.rect.width, region.rect.y + region.rect.height],
  ]
  return points.find(([, px, py]) => Math.hypot(px - x, py - y) <= tolerance)?.[0] ?? null
}

function resizeFromHandle(initial: Rect, handle: Handle, dx: number, dy: number): Rect {
  if (handle === 'nw') return normalizeRect({ x: initial.x + dx, y: initial.y + dy, width: initial.width - dx, height: initial.height - dy })
  if (handle === 'ne') return normalizeRect({ x: initial.x, y: initial.y + dy, width: initial.width + dx, height: initial.height - dy })
  if (handle === 'sw') return normalizeRect({ x: initial.x + dx, y: initial.y, width: initial.width - dx, height: initial.height + dy })
  return normalizeRect({ x: initial.x, y: initial.y, width: initial.width + dx, height: initial.height + dy })
}

export function EditorCanvas({ decoded, regions, selectedId, manualMode, zoom, options, showOriginal, onSelect, onCommit }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const basePreviewRef = useRef<{ decoded: DecodedImage; regions: DetectionRegion[]; options: ExportOptions; showOriginal: boolean; width: number; height: number; canvas: HTMLCanvasElement } | null>(null)
  const [draft, setDraft] = useState(() => cloneRegions(regions))
  const draftRef = useRef(draft)
  const interactionRef = useRef<Interaction | null>(null)
  const longEdge = Math.max(decoded.width, decoded.height)
  const renderScale = Math.min(1, 2200 / longEdge)
  const renderWidth = Math.max(1, Math.round(decoded.width * renderScale))
  const renderHeight = Math.max(1, Math.round(decoded.height * renderScale))
  const selected = useMemo(() => draft.find((region) => region.id === selectedId), [draft, selectedId])

  useEffect(() => { const next = cloneRegions(regions); draftRef.current = next; setDraft(next) }, [regions])
  useEffect(() => () => { if (basePreviewRef.current) { basePreviewRef.current.canvas.width = 1; basePreviewRef.current.canvas.height = 1; basePreviewRef.current = null } }, [])
  useEffect(() => { draftRef.current = draft }, [draft])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.width = renderWidth
    canvas.height = renderHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, renderWidth, renderHeight)
    let base = basePreviewRef.current
    if (!base
      || base.decoded !== decoded
      || base.regions !== regions
      || base.options !== options
      || base.showOriginal !== showOriginal
      || base.width !== renderWidth
      || base.height !== renderHeight) {
      if (base) { base.canvas.width = 1; base.canvas.height = 1 }
      const source = document.createElement('canvas')
      source.width = renderWidth
      source.height = renderHeight
      source.getContext('2d')?.drawImage(decoded.drawable, 0, 0, renderWidth, renderHeight)
      const preview = document.createElement('canvas')
      preview.width = renderWidth
      preview.height = renderHeight
      const previewCtx = preview.getContext('2d')
      if (showOriginal) {
        previewCtx?.drawImage(source, 0, 0)
      } else {
        const scaledRegions = regions.map((region) => ({
          ...region,
          rect: {
            x: region.rect.x * renderScale,
            y: region.rect.y * renderScale,
            width: region.rect.width * renderScale,
            height: region.rect.height * renderScale,
          },
        }))
        const redacted = renderRedactedCanvas(source, renderWidth, renderHeight, scaledRegions, options)
        previewCtx?.drawImage(redacted, 0, 0)
        redacted.width = 1
        redacted.height = 1
      }
      source.width = 1
      source.height = 1
      base = { decoded, regions, options, showOriginal, width: renderWidth, height: renderHeight, canvas: preview }
      basePreviewRef.current = base
    }
    ctx.drawImage(base.canvas, 0, 0)

    for (const region of draft.filter((item) => item.enabled)) {
      const rect = {
        x: region.rect.x * renderScale,
        y: region.rect.y * renderScale,
        width: region.rect.width * renderScale,
        height: region.rect.height * renderScale,
      }
      const isSelected = region.id === selectedId
      ctx.save()
      ctx.fillStyle = isSelected ? 'rgba(0,113,227,.18)' : 'rgba(29,29,31,.12)'
      ctx.strokeStyle = isSelected ? '#ffffff' : '#0a84ff'
      ctx.lineWidth = isSelected ? 4 : 2
      ctx.setLineDash(region.source === 'manual' ? [] : [8, 5])
      ctx.fillRect(rect.x, rect.y, rect.width, rect.height)
      ctx.strokeRect(rect.x, rect.y, rect.width, rect.height)
      if (isSelected) {
        ctx.setLineDash([])
        const corners: Array<[number, number]> = [
          [rect.x, rect.y],
          [rect.x + rect.width, rect.y],
          [rect.x, rect.y + rect.height],
          [rect.x + rect.width, rect.y + rect.height],
        ]
        for (const [x, y] of corners) {
          ctx.fillStyle = '#ffffff'
          ctx.strokeStyle = '#0071e3'
          ctx.lineWidth = 2
          ctx.fillRect(x - 7, y - 7, 14, 14)
          ctx.strokeRect(x - 7, y - 7, 14, 14)
        }
      }
      ctx.restore()
    }
  }, [decoded, draft, options, regions, renderHeight, renderScale, renderWidth, selectedId, showOriginal])

  const pointFromEvent = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    return {
      x: ((event.clientX - rect.left) / rect.width) * decoded.width,
      y: ((event.clientY - rect.top) / rect.height) * decoded.height,
    }
  }

  const pointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const point = pointFromEvent(event)
    const tolerance = 18 / renderScale

    if (manualMode) {
      const id = createId('manual')
      const region: DetectionRegion = {
        id,
        rect: { x: point.x, y: point.y, width: 1, height: 1 },
        category: 'other',
        source: 'manual',
        label: '手動で追加',
        confidence: 1,
        enabled: true,
      }
      const next = [...draftRef.current, region]
      draftRef.current = next
      setDraft(next)
      onSelect(id)
      event.currentTarget.setPointerCapture(event.pointerId)
      interactionRef.current = { kind: 'create', startX: point.x, startY: point.y, regionId: id }
      return
    }

    if (selected) {
      const handle = findHandle(selected, point.x, point.y, tolerance)
      if (handle) {
        event.currentTarget.setPointerCapture(event.pointerId)
        interactionRef.current = { kind: 'resize', startX: point.x, startY: point.y, initial: { ...selected.rect }, regionId: selected.id, handle }
        return
      }
    }

    const hit = [...draft].reverse().find((region) => region.enabled && rectContains(region.rect, point.x, point.y))
    if (hit) {
      onSelect(hit.id)
      event.currentTarget.setPointerCapture(event.pointerId)
      interactionRef.current = { kind: 'move', startX: point.x, startY: point.y, initial: { ...hit.rect }, regionId: hit.id }
    } else {
      onSelect(undefined)
    }
  }

  const pointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const interaction = interactionRef.current
    if (!interaction) return
    const point = pointFromEvent(event)
    const dx = point.x - interaction.startX
    const dy = point.y - interaction.startY
    const next = draftRef.current.map((region) => {
      if (region.id !== interaction.regionId) return region
      let rect: Rect
      if (interaction.kind === 'create') rect = normalizeRect({ x: interaction.startX, y: interaction.startY, width: dx, height: dy })
      else if (interaction.kind === 'move') rect = { ...interaction.initial, x: interaction.initial.x + dx, y: interaction.initial.y + dy }
      else rect = resizeFromHandle(interaction.initial, interaction.handle, dx, dy)
      return { ...region, rect: clampRect(rect, decoded.width, decoded.height) }
    })
    draftRef.current = next
    setDraft(next)
  }

  const pointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!interactionRef.current) return
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
    interactionRef.current = null
    const cleaned = draftRef.current.filter((region) => isUsableRect(region.rect))
    draftRef.current = cleaned
    setDraft(cleaned)
    onCommit(cleaned)
  }

  return (
    <div className="editor-scroll" aria-label="画像編集エリア">
      <canvas
        ref={canvasRef}
        className={`editor-canvas ${manualMode ? 'manual-cursor' : ''} ${selected ? 'editing-cursor' : ''}`}
        style={{ width: `${zoom}%`, aspectRatio: `${renderWidth}/${renderHeight}` }}
        onPointerDown={pointerDown}
        onPointerMove={pointerMove}
        onPointerUp={pointerUp}
        onPointerCancel={pointerUp}
      />
    </div>
  )
}
